import { useState } from 'react'
import {
  Plus,
  MoreVertical,
  Mail,
  Trash2,
  Edit,
  Search,
  X,
  Check,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import api from '@/services/api'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface User {
  id: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'technician' | 'viewer'
  status: 'active' | 'invited' | 'inactive'
  avatar?: string
  lastActive?: Date
  queriesThisMonth: number
}

const sampleUsers: User[] = [
  {
    id: '1',
    name: 'John Martinez',
    email: 'john.martinez@acmeair.com',
    role: 'owner',
    status: 'active',
    lastActive: new Date(Date.now() - 1000 * 60 * 5),
    queriesThisMonth: 342,
  },
  {
    id: '2',
    name: 'Sarah Chen',
    email: 'sarah.chen@acmeair.com',
    role: 'admin',
    status: 'active',
    lastActive: new Date(Date.now() - 1000 * 60 * 30),
    queriesThisMonth: 256,
  },
  {
    id: '3',
    name: 'Mike Thompson',
    email: 'mike.t@acmeair.com',
    role: 'technician',
    status: 'active',
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2),
    queriesThisMonth: 189,
  },
  {
    id: '4',
    name: 'Lisa Wong',
    email: 'lwong@acmeair.com',
    role: 'technician',
    status: 'invited',
    queriesThisMonth: 0,
  },
  {
    id: '5',
    name: 'David Park',
    email: 'dpark@acmeair.com',
    role: 'viewer',
    status: 'active',
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3),
    queriesThisMonth: 45,
  },
]

export function UsersPage() {
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'technician' | 'admin' | 'viewer'>('technician')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState(false)
  const [users, setUsers] = useState<User[]>(sampleUsers)

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleInvite = async () => {
    if (!inviteEmail) {
      setInviteError('Please enter an email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setInviteError('Please enter a valid email address')
      return
    }

    setInviting(true)
    setInviteError(null)

    try {
      await api.post('/api/admin/users/invite', { email: inviteEmail, role: inviteRole })

      // Add user to local state as invited
      const newUser: User = {
        id: Date.now().toString(),
        name: inviteEmail.split('@')[0],
        email: inviteEmail,
        role: inviteRole,
        status: 'invited',
        queriesThisMonth: 0,
      }
      setUsers(prev => [...prev, newUser])

      setInviteSuccess(true)
      setTimeout(() => {
        handleCloseInviteModal()
      }, 2000)
    } catch (error) {
      setInviteError(error instanceof Error ? error.message : 'Failed to send invitation')
    } finally {
      setInviting(false)
    }
  }

  const handleRemoveUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user?')) {
      return
    }

    try {
      await api.delete(`/api/admin/users/${userId}`)
      setUsers(prev => prev.filter(u => u.id !== userId))
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to remove user')
    }
  }

  const handleEditRole = async (userId: string, newRole: User['role']) => {
    try {
      await api.patch(`/api/admin/users/${userId}/role`, { role: newRole })
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u))
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to update role')
    }
  }

  const handleResendInvite = async (email: string) => {
    try {
      await api.post('/api/admin/users/resend-invite', { email })
      alert('Invitation resent successfully')
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to resend invitation')
    }
  }

  const handleCloseInviteModal = () => {
    setShowInviteModal(false)
    setInviteEmail('')
    setInviteRole('technician')
    setInviteError(null)
    setInviteSuccess(false)
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return <Badge className="bg-purple-100 text-purple-800">Owner</Badge>
      case 'admin':
        return <Badge className="bg-blue-100 text-blue-800">Admin</Badge>
      case 'technician':
        return <Badge className="bg-green-100 text-green-800">Technician</Badge>
      case 'viewer':
        return <Badge variant="secondary">Viewer</Badge>
      default:
        return null
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <div className="flex items-center gap-1 text-green-600">
            <Check className="h-3 w-3" />
            <span className="text-xs">Active</span>
          </div>
        )
      case 'invited':
        return (
          <div className="flex items-center gap-1 text-yellow-600">
            <Clock className="h-3 w-3" />
            <span className="text-xs">Pending</span>
          </div>
        )
      case 'inactive':
        return (
          <div className="flex items-center gap-1 text-gray-400">
            <X className="h-3 w-3" />
            <span className="text-xs">Inactive</span>
          </div>
        )
      default:
        return null
    }
  }

  const formatLastActive = (date?: Date) => {
    if (!date) return 'Never'
    const diff = Date.now() - date.getTime()
    const minutes = Math.floor(diff / 1000 / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (minutes < 5) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600">
            Manage your organization's users and permissions
          </p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">
              {users.length}
            </div>
            <div className="text-sm text-gray-500">Total Members</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {users.filter((u) => u.status === 'active').length}
            </div>
            <div className="text-sm text-gray-500">Active</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {users.filter((u) => u.status === 'invited').length}
            </div>
            <div className="text-sm text-gray-500">Pending Invites</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">
              {users.reduce((acc, u) => acc + u.queriesThisMonth, 0)}
            </div>
            <div className="text-sm text-gray-500">Queries This Month</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <div className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Queries
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y bg-white">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback className="bg-navy-900 text-white text-sm">
                          {user.name.split(' ').map((n) => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                  <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500">
                      {formatLastActive(user.lastActive)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {user.queriesThisMonth}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.role !== 'owner' && (
                          <>
                            {(['admin', 'technician', 'viewer'] as const)
                              .filter(r => r !== user.role)
                              .map(role => (
                                <DropdownMenuItem
                                  key={role}
                                  onClick={() => handleEditRole(user.id, role)}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Change to {role.charAt(0).toUpperCase() + role.slice(1)}
                                </DropdownMenuItem>
                              ))}
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => window.open(`mailto:${user.email}`, '_blank')}
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Send Email
                        </DropdownMenuItem>
                        {user.status === 'invited' && (
                          <DropdownMenuItem onClick={() => handleResendInvite(user.email)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Resend Invite
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {user.role !== 'owner' && (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleRemoveUser(user.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Invite Team Member</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCloseInviteModal}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <CardDescription>
                Send an invitation to join your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Success Message */}
              {inviteSuccess && (
                <div className="rounded-md bg-green-50 p-4 text-green-700 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Invitation sent successfully!</span>
                </div>
              )}

              {/* Error Message */}
              {inviteError && (
                <div className="rounded-md bg-red-50 p-4 text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  <span>{inviteError}</span>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  className="mt-1"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={inviting || inviteSuccess}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Role</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'technician' | 'admin' | 'viewer')}
                  disabled={inviting || inviteSuccess}
                >
                  <option value="technician">Technician</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Technicians can query manuals. Admins can also manage users
                  and settings.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCloseInviteModal}
                  disabled={inviting}
                >
                  Cancel
                </Button>
                <Button onClick={handleInvite} disabled={inviting || inviteSuccess}>
                  {inviting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
